import { TextField } from '@mui/material';
import { ChangeEvent, forwardRef, KeyboardEvent, ReactNode } from 'react';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';
import { withTestId } from 'utils/utils';

type Props = {
  value: string;
  disabled: boolean;
  onChange: (text: string) => void;
  onSend: (text: string) => void;

  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  ariaLabel?: string;
  minRows?: number;
  maxRows?: number;
  children?: ReactNode;
  testId?: string;
  autoFocus?: boolean;
};

export const GenericMessageInput = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      value,
      disabled,
      onChange,
      onSend,
      onKeyDown,
      placeholder,
      ariaLabel,
      minRows = 1,
      maxRows = 5,
      children,
      testId,
      autoFocus
    },
    ref
  ) => {
    const translate = useTr();

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.shiftKey || e.key !== 'Enter') {
        return;
      }

      e.preventDefault();
      onSend(value);
    };

    const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.currentTarget.value);
    };

    return (
      <InputContainer>
        <MainInputArea>
          <StyledTextField
            value={value}
            onChange={handleTextFieldChange}
            onKeyDown={onKeyDown || handleKeyDown}
            aria-label={ariaLabel || translate('chat.message_input.aria_label')}
            placeholder={placeholder || translate('chat.message_input.placeholder')}
            disabled={disabled}
            multiline={true}
            minRows={minRows}
            maxRows={maxRows}
            inputRef={ref}
            fullWidth={true}
            autoFocus={autoFocus}
            {...(testId ? withTestId(testId) : {})}
          />
        </MainInputArea>

        {children && <ToolsBox>{children}</ToolsBox>}
      </InputContainer>
    );
  }
);

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? theme.colors.backgroundPaperElevation2 : theme.colors.commonWhiteMain};
  border-radius: ${({ theme }) => theme.borderRadius.one};
  border: 1px solid ${({ theme }) => theme.colors.divider};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primaryColorMain};
  }
`;

const MainInputArea = styled.div`
  display: flex;
  flex: 1;
`;

const ToolsBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacings.one} ${theme.spacings.two}`};
  border-radius: ${({ theme }) => `0 0 ${theme.borderRadius.one} ${theme.borderRadius.one}`};
`;

const StyledTextField = styled(TextField)`
  flex: 1;

  .MuiInputBase-root {
    border: none;
    border-radius: 50%;

    /* Remove default border and focus styles since we handle them in the container */
    &:before,
    &:after {
      display: none;
    }

    &.Mui-focused {
      border: none;
      box-shadow: none;
    }

    textarea {
      margin: 0;
      border: none;
      outline: none;
    }
  }

  /* Remove any default TextField styling */
  .MuiOutlinedInput-notchedOutline {
    display: none;
  }
` as typeof TextField;
